import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { IPagedResponse } from "shared/interfaces/api";

// Interfaces e serviços para CEM MOBI
export interface IInquilinoMOBI {
  id: string;
  razaoSocial: string;
  nomeFantasia: string;
  documento: string;
  cidade: string;
  estado: string;
}

export interface ILiberacaoTipologiaMOBI {
  id: number;
  inquilinoId: string;
  inquilinoNome: string;
  projetistaId: number;
  projetistaNome: string;
  linhaId?: number;
  linhaNome?: string;
  dataLiberacao: string;
  situacao: string;
  observacoes?: string;
  sincronizadoMOBI: boolean;
  dataSincronizacaoMOBI?: string;
}

export interface ILiberacaoReferenciaMOBI {
  id: number;
  inquilinoId: string;
  inquilinoNome: string;
  referenciaId: number;
  referenciaCodigo: string;
  tipoPacote: string;
  linhaId?: number;
  linhaNome?: string;
  dataLiberacao: string;
  situacao: string;
  observacoes?: string;
  sincronizadoMOBI: boolean;
  dataSincronizacaoMOBI?: string;
}

export interface ICriarLiberacaoTipologiaMOBI {
  inquilinoId: string;
  projetistaId: number;
  linhaId?: number;
  observacoes?: string;
}

export interface ICriarLiberacaoReferenciaMOBI {
  inquilinoId: string;
  referenciaId: number;
  linhaId?: number;
  observacoes?: string;
}

export interface IReferenciaDisponivelMOBI {
  referenciaId: number;
  referenciaCodigo: string;
  referenciaDescricao: string;
  linhaId?: number;
  linhaNome?: string;
  tipoMaterial: string;
  dataDisponibilizacao: string;
  jaLiberado: boolean;
}

export interface ITipologiaDisponivelMOBI {
  projetistaId: number;
  projetistaNome: string;
  linhaId: number;
  linhaNome: string;
  dataDisponibilizacao: string;
  jaLiberado: boolean;
}

export interface IListarLiberacoesTipologiaMOBIParams {
  inquilinoId?: string;
  projetistaId?: number;
  linhaId?: number;
  page?: number;
  limit?: number;
}

export interface IListarLiberacoesReferenciaMOBIParams {
  inquilinoId?: string;
  referenciaId?: number;
  linhaId?: number;
  page?: number;
  limit?: number;
}

export interface IListarInquilinosMOBIParams {
  pageIndex?: number;
  pageSize?: number;
  sort?: string;
  desc?: boolean;
  globalFilter?: string;
}

// Serviços para CEM MOBI
export const listarInquilinosMOBI = async (params: IListarInquilinosMOBIParams) => {
  try {
    const queryParams = {
      pageIndex: params.pageIndex ?? 0,
      pageSize: params.pageSize ?? 10,
      sort: params.sort,
      desc: params.desc ?? false,
      globalFilter: params.globalFilter,
    };

    const { data } = await api.get<{ dados: IInquilinoMOBI[]; paginas: number }>(
      "/Liberacao/MOBI/Inquilinos",
      { params: queryParams }
    );

    return {
      dados: data?.dados ?? [],
      paginas: data?.paginas ?? 0,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao carregar inquilinos do CEM MOBI.");
    return {
      dados: [],
      paginas: 0,
    };
  }
};

export const listarLiberacoesTipologiaMOBI = async (
  params: IListarLiberacoesTipologiaMOBIParams
) => {
  try {
    const queryParams = {
      inquilinoId: params.inquilinoId,
      projetistaId: params.projetistaId,
      linhaId: params.linhaId,
      page: params.page ?? 1,
      limit: params.limit ?? 10,
    };

    const { data } = await api.get<IPagedResponse<ILiberacaoTipologiaMOBI>>(
      "/Liberacao/MOBI/Tipologia",
      { params: queryParams }
    );

    return (
      data ?? {
        items: [],
        page: 1,
        totalPages: 0,
        totalRecords: 0,
        paginate: true,
      }
    );
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao carregar liberações de tipologias MOBI.");
    return {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    } as IPagedResponse<ILiberacaoTipologiaMOBI>;
  }
};

export const criarLiberacaoTipologiaMOBI = async (
  payload: ICriarLiberacaoTipologiaMOBI
) => {
  try {
    const { data } = await api.post<number>("/Liberacao/MOBI/Tipologia", payload);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao criar liberação de tipologia MOBI.");
    return null;
  }
};

export const removerLiberacaoTipologiaMOBI = async (liberacaoId: number) => {
  try {
    const { data } = await api.delete<boolean>(
      `/Liberacao/MOBI/Tipologia/${liberacaoId}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover liberação de tipologia MOBI.");
    return false;
  }
};

export const listarLiberacoesReferenciaMOBI = async (
  params: IListarLiberacoesReferenciaMOBIParams
) => {
  try {
    const queryParams = {
      inquilinoId: params.inquilinoId,
      referenciaId: params.referenciaId,
      linhaId: params.linhaId,
      page: params.page ?? 1,
      limit: params.limit ?? 10,
    };

    const { data } = await api.get<IPagedResponse<ILiberacaoReferenciaMOBI>>(
      "/Liberacao/MOBI/Referencia",
      { params: queryParams }
    );

    return (
      data ?? {
        items: [],
        page: 1,
        totalPages: 0,
        totalRecords: 0,
        paginate: true,
      }
    );
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao carregar liberações de referências MOBI.");
    return {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    } as IPagedResponse<ILiberacaoReferenciaMOBI>;
  }
};

export const criarLiberacaoReferenciaMOBI = async (
  payload: ICriarLiberacaoReferenciaMOBI
) => {
  try {
    const { data } = await api.post<number>("/Liberacao/MOBI/Referencia", payload);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao criar liberação de referência MOBI.");
    return null;
  }
};

export const removerLiberacaoReferenciaMOBI = async (liberacaoId: number) => {
  try {
    const { data } = await api.delete<boolean>(
      `/Liberacao/MOBI/Referencia/${liberacaoId}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover liberação de referência MOBI.");
    return false;
  }
};

// Serviços para listar conteúdos disponíveis
export const listarReferenciasDisponiveisMOBI = async (params: {
  inquilinoId?: string;
  filtro?: string;
  linhaId?: number;
  page?: number;
  limit?: number;
}) => {
  try {
    const queryParams = {
      inquilinoId: params.inquilinoId,
      filtro: params.filtro,
      linhaId: params.linhaId,
      page: params.page ?? 1,
      limit: params.limit ?? 10,
    };

    const { data } = await api.get<IPagedResponse<IReferenciaDisponivelMOBI>>(
      "/Liberacao/MOBI/ReferenciasDisponiveis",
      { params: queryParams }
    );

    return (
      data ?? {
        items: [],
        page: 1,
        totalPages: 0,
        totalRecords: 0,
        paginate: true,
      }
    );
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao carregar referências disponíveis.");
    return {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    } as IPagedResponse<IReferenciaDisponivelMOBI>;
  }
};

export const listarTipologiasDisponiveisMOBI = async (params: {
  inquilinoId?: string;
  filtro?: string;
  projetistaId?: number;
  linhaId?: number;
  page?: number;
  limit?: number;
}) => {
  try {
    const queryParams = {
      inquilinoId: params.inquilinoId,
      filtro: params.filtro,
      projetistaId: params.projetistaId,
      linhaId: params.linhaId,
      page: params.page ?? 1,
      limit: params.limit ?? 10,
    };

    const { data } = await api.get<IPagedResponse<ITipologiaDisponivelMOBI>>(
      "/Liberacao/MOBI/TipologiasDisponiveis",
      { params: queryParams }
    );

    return (
      data ?? {
        items: [],
        page: 1,
        totalPages: 0,
        totalRecords: 0,
        paginate: true,
      }
    );
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao carregar tipologias disponíveis.");
    return {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    } as IPagedResponse<ITipologiaDisponivelMOBI>;
  }
};

